// modules
import { get } from 'lodash'
import { createSelector } from 'reselect'

import { patientSelector } from 'src/store/modules/patient/selectors'

// utils
import { convertDataFields, convertFrequencyList } from 'src/utils/DataTrees'

const updateRuleList = (itemList = []) =>
  itemList
    .map((item) => ({
      ...item,
      escalate: !!item.alertPriority,
      alertPriority: +item.alertPriority === 0 ? null : +item.alertPriority,
      patientAlertText: item.patientAlertText ? item.patientAlertText : 'None'
    }))
    .sort((a, b) => +b.type - +a.type)

export const patientMedicationList$ = createSelector(
  patientSelector,
  (profile) => profile.medications.patientMedicationList
)

export const patientDashboardMedicationList$ = createSelector(
  patientSelector,
  (profile) => profile.medications.patientDashboardMedicationList
)

export const medicationActiveTab$ = createSelector(
  patientSelector,
  (profile) => profile.medications.medicationActiveTab
)

export const carePlanActiveTab$ = createSelector(
  patientSelector,
  (profile) => profile.medications.carePlanActiveTab
)

export const medicationReferer$ = createSelector(
  patientSelector,
  (profile) => profile.medications.medicationReferer
)

export const patientMedicationValue$ = createSelector(
  patientSelector,
  (profile) => {
    const patientMedicationValue = profile.medications.patientMedicationValue
    const rules = get(patientMedicationValue, 'rules', [])
    const frequency = get(patientMedicationValue, 'frequency', {})

    return {
      ...patientMedicationValue,
      rules: updateRuleList(rules),
      frequency: convertFrequencyList(frequency)
    }
  }
)

export const intakeList$ = createSelector(patientSelector, (profile) =>
  profile.medications.intakeList.map((item, key) => ({ ...item, key }))
)

export const intakeValue$ = createSelector(
  patientSelector,
  (profile) => profile.medications.intakeValue
)

export const medicationList$ = createSelector(patientSelector, (profile) =>
  profile.medications.medicationList.map((item, key) => ({
    ...item,
    key,
    title: item.name,
    autocompleteTitle: item.dosageType
      ? `${item.name} (${item.dosageType})`
      : item.name,
    value: item.uuid
  }))
)

export const assignedMedicationList$ = createSelector(
  patientSelector,
  (profile) =>
    profile.medications.assignedMedicationList.map((item, key) => {
      const { length: nameLength } = item.name
      const { length: dosageLength } = item.dosage
      let newTitle =
        nameLength > 32 ? `${item.name.slice(0, 32)}...` : item.name
      newTitle = `${newTitle} (${
        dosageLength > 32 ? `${item.dosage.slice(0, 32)}...` : item.dosage
      })`

      return {
        ...item,
        key,
        title: newTitle,
        value: item.uuid,
        isActive: !!+item.isActive
      }
    })
)

export const medicationRuleList$ = createSelector(patientSelector, (profile) =>
  updateRuleList(profile.medications.medicationRuleList)
)

export const orderingList$ = createSelector(patientSelector, (profile) => {
  const updOrderingList = [
    { title: 'None', uuid: null },
    ...get(profile, 'medications.orderingList', [])
  ]

  return convertDataFields(updOrderingList)
})

export const pharmacyList$ = createSelector(patientSelector, (profile) => {
  const updPharmacyList = [
    { title: 'None', uuid: null },
    ...get(profile, 'medications.pharmacyList', [])
  ]

  return convertDataFields(updPharmacyList)
})

export const searchLoading$ = createSelector(
  patientSelector,
  (profile) => profile.medications.searchLoading
)

export const dosageTypes$ = createSelector(
  patientSelector,
  (profile) => profile.medications.dosageTypes
)

export const medicationPredefinedFilter$ = createSelector(
  patientSelector,
  (profile) => profile.medications.medicationPredefinedFilter
)
